import { useLayoutEffect, useMemo, useRef } from 'react';
import { Box, Card, CircularProgress, IconButton, Typography, useTheme, alpha, SxProps, Theme } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import { Button } from '@baseComponents';
import { useDropzone } from 'react-dropzone';
import { ImageFileSummary, ACCEPTED_TYPES } from './imageFileUtils';


// An SVG rect (fill: none) used as a background image so the dashed border
// honors DASH_LENGTH/DASH_GAP.
const DASH_LENGTH = 10;
const DASH_GAP = 8;
const DASH_STROKE = 1;
const DASH_RADIUS = 8;
const DASH_INSET = 2;
const dashedBorderImage = (color: string | undefined) =>
  `url("data:image/svg+xml,${encodeURIComponent(
    `<svg xmlns='http://www.w3.org/2000/svg' width='100%' height='100%'>` +
      `<rect x='${DASH_INSET}' y='${DASH_INSET}' ` +
      `width='calc(100% - ${2 * DASH_INSET}px)' height='calc(100% - ${2 * DASH_INSET}px)' ` +
      `fill='none' rx='${DASH_RADIUS - DASH_INSET}' ry='${DASH_RADIUS - DASH_INSET}' ` +
      `stroke='${color ?? 'currentColor'}' stroke-width='${DASH_STROKE}' ` +
      `stroke-dasharray='${DASH_LENGTH} ${DASH_GAP}'/>` +
      `</svg>`
  )}")`;

interface ImagesPanelProps {
  images: ImageFileSummary[];
  onRemoveImage: (name: string) => void;
  onDrop: (acceptedFiles: File[]) => void;
  isProcessing: boolean;
  selectedImageName: string | null;
  onSelectImage: (name: string) => void;
  // Images with an in-flight match request
  matchingImageNames: string[];
  // Native drag of a photo card onto a user row: start/end report the dragged
  // image so the parent can offer matching drop targets in the users panel.
  onImageDragStart: (name: string) => void;
  onImageDragEnd: () => void;
}

const ImagesPanel = ({
  images,
  onRemoveImage,
  onDrop,
  isProcessing,
  selectedImageName,
  onSelectImage,
  matchingImageNames,
  onImageDragStart,
  onImageDragEnd
}: ImagesPanelProps) => {
  const theme = useTheme();

  // The panel itself is the dropzone. `noClick` keeps clicks on thumbnails from opening the file dialog;
  // The empty state wires up `open` explicitly instead.
  const { getRootProps, getInputProps, open, isDragAccept, isDragReject } = useDropzone({
    onDrop,
    accept: ACCEPTED_TYPES,
    multiple: true,
    noClick: true,
    disabled: isProcessing
  });
  // FLIP animation bookkeeping: the grid container, live DOM nodes keyed by image
  // name, plus each node's position (relative to the grid) from the previous render.
  const gridRef = useRef<HTMLDivElement>(null);
  const photoNodesRef = useRef<Map<string, HTMLDivElement>>(new Map());
  const photoRectsRef = useRef<Map<string, { top: number; left: number }>>(new Map());

  // FLIP: after the photo list changes, slide any thumbnail that moved from its
  // previous slot to its new one. New thumbnails (no prior position) don't animate.
  useLayoutEffect(() => {
    const gridEl = gridRef.current;
    if (!gridEl) return;
    // Measure positions relative to the grid's scrolled content (not the viewport),
    // so neither page scrolling nor the grid's own scroll gets baked into the delta.
    const gridRect = gridEl.getBoundingClientRect();
    const prevRects = photoRectsRef.current;
    const nextRects = new Map<string, { top: number; left: number }>();
    photoNodesRef.current.forEach((node, name) => {
      const rect = node.getBoundingClientRect();
      const pos = {
        top: rect.top - gridRect.top + gridEl.scrollTop,
        left: rect.left - gridRect.left + gridEl.scrollLeft
      };
      nextRects.set(name, pos);
      const prev = prevRects.get(name);
      if (!prev) return;
      const dx = prev.left - pos.left;
      const dy = prev.top - pos.top;
      if (dx || dy) {
        node.animate(
          [{ transform: `translate(${dx}px, ${dy}px)` }, { transform: 'translate(0px, 0px)' }],
          // Brief pause so the removed thumbnail clears before the rest slide in.
          // `fill: backwards` holds the old position during the delay (no jump).
          { duration: 280, easing: 'ease', delay: 80, fill: 'backwards' }
        );
      }
    });
    photoRectsRef.current = nextRects;
  }, [images]);

  const styles = useMemo(
    () =>
      ({
        // The panel doubles as the dropzone. Border width is static; its style
        // (dashed/solid) and color are computed per drag state in `dragSx` below.
        dropzone: {
          flex: 1,
          borderRadius: 2,
          minHeight: 0,
          height: '100%',
          p: 2,
          display: 'flex',
          flexDirection: 'column',
          borderWidth: 1,
          outline: 'none',
          transition:
            'border-color .24s ease-in-out, border-style .24s ease-in-out, background-color .24s ease-in-out'
        },
        cardHeader: {
          mb: 2,
          flexShrink: 0,
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 2
        },
        headerUploadIcon: {
          fontSize: 28,
          marginRight: 1,
        },
        emptyState: {
          flex: 1,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          textAlign: 'center',
          cursor: 'pointer'
        },
        uploadIcon: {
          fontSize: 32,
          color: theme.palette.grey[700],
          mb: 1
        },
        photosGrid: {
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))',
          gridAutoRows: 'max-content',
          gap: 2,
          alignContent: 'start',
          alignItems: 'start',
        },
        photoCard: {
          position: 'relative',
          borderRadius: 2,
          // Draggable onto a user row; click still selects. `grab` hints the drag.
          cursor: 'grab',
          '&:active': { cursor: 'grabbing' },
          // Reveal the delete button only while hovering the thumbnail
          '&:hover .photo-delete-btn': { opacity: 1 }
        },
        photoCardSelected: {
          border: `solid 2px ${theme.palette.blue[300]}`
        },
        // While a match is saving: no interactions, just the overlay below
        photoCardMatching: {
          cursor: 'default',
          pointerEvents: 'none'
        },
        // Subtle grey scrim over an image whose match is in flight
        photoOverlay: {
          position: 'absolute',
          inset: 0,
          borderRadius: 2,
          zIndex: 2,
          backgroundColor: alpha(theme.palette.common.black, 0.08)
        },
        // Filled X in the thumbnail's top-right corner; turns red when hovered directly
        photoDeleteButton: {
          position: 'absolute',
          top: 4,
          right: 4,
          zIndex: 1,
          padding: 0,
          opacity: 0,
          transition: 'opacity 0.15s ease-in-out',
          '&:hover': { backgroundColor: 'transparent' },
          // Direct hover over the icon: red fill, white X
          '&:hover .photo-delete-circle': {
            backgroundColor: theme.palette.error.main,
            color: theme.palette.common.white
          },
          '&:focus-visible': { opacity: 1 }
        },
        // Colored disk; X color is inherited via `color`
        photoDeleteCircle: {
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 24,
          height: 24,
          borderRadius: '50%',
          backgroundColor: theme.palette.grey[100],
          color: theme.palette.grey[700],
          transition: 'background-color 0.15s ease-in-out, color 0.15s ease-in-out'
        },
        photoDeleteIcon: { fontSize: 18 },
        photoImage: {
          width: '100%',
          aspectRatio: '1 / 1',
          objectFit: 'cover',
          display: 'block'
        },
        photoMeta: { p: 1 }
      }) satisfies Record<string, SxProps<Theme>>,
    [theme]
  );

  const setPhotoNodeRef = (name: string) => (node: HTMLDivElement | null) => {
    if (node) {
      photoNodesRef.current.set(name, node);
    } else {
      photoNodesRef.current.delete(name);
    }
  };

  const isDragActive = isDragAccept || isDragReject;
  const isSolid = images.length > 0 && !isDragActive;
  const borderColor = isDragReject
    ? theme.palette.error.main
    : theme.palette.blueGrey[500]

  const backgroundColor = isDragAccept
    ? theme.palette.blueGrey[100]
    : isDragReject
      ? alpha(theme.palette.error.light, 0.2)
      : 'transparent';
  // Solid state uses a native border; dashed state swaps it for the SVG image so
  // the dash length and gap are controlled by DASH_LENGTH/DASH_GAP.
  const dragSx: SxProps<Theme> = isSolid
    ? { borderStyle: 'solid', borderColor, backgroundColor }
    : {
        border: 'none',
        backgroundColor,
        backgroundImage: dashedBorderImage(borderColor),
        backgroundRepeat: 'no-repeat',
        // The SVG has no viewBox/intrinsic size, so pin it to the element's exact
        // box — otherwise it paints at the 300x150 fallback size and the right
        // (and bottom) edge falls outside the element and gets clipped.
        backgroundSize: '100% 100%'
      };

  return (
    <Card variant='outlined' {...getRootProps()} sx={[styles.dropzone, dragSx]}>
      <input {...getInputProps()} />

      {images.length === 0 ? (
        isProcessing ? (
          <Box sx={[styles.emptyState, { cursor: 'default' }]}>
            <CircularProgress size={32} sx={{ mb: 1.5 }} />
            <Typography variant='bodyMedium' sx={{
              fontWeight: '600'
            }}>
              Processing images…
            </Typography>
          </Box>
        ) : (
          <Box sx={styles.emptyState} onClick={open} aria-label='image upload drop zone'>
            <CloudUploadOutlinedIcon sx={styles.uploadIcon} />
            <Typography variant='bodyMedium' sx={{
              fontWeight: '600'
            }}>
              Drag photos here, or browse files
            </Typography>
            <Typography variant='bodySmall' sx={{
              color: 'text.secondary'
            }}>
              JPG, PNG, or ZIP &bull; multiple files OK
            </Typography>
          </Box>
        )
      ) : (
        <>
          <Box sx={styles.cardHeader}>
            <Box>
              <Typography variant='bodyMedium' sx={{
                fontWeight: '600'
              }}>
                Unmapped photos
              </Typography>
              <Typography
                variant='bodySmall'
                sx={{
                  color: 'text.secondary',
                  mt: '4px'
                }}>
                {images.length} unmapped
              </Typography>
              <Typography
                variant='bodySmall'
                sx={{
                  color: 'text.secondary',
                  mt: '2px'
                }}>
                Drag a photo onto a user, or click to select then pick a user.
              </Typography>
            </Box>
            {isProcessing ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={20} />
                <Typography variant='bodySmall' sx={{
                  color: 'text.secondary'
                }}>
                  Processing images…
                </Typography>
              </Box>
            ) : (
              <Button
                mode='text'
                onClick={open}
                aria-label='Upload more photos'
                startIcon={<CloudUploadOutlinedIcon sx={styles.headerUploadIcon} />}
                text='Click to browse or drag'
              />
            )}
          </Box>

          <Box sx={styles.photosGrid} ref={gridRef}>
            {images.map((image) => {
              const isMatching = matchingImageNames.includes(image.name);
              return (
                <Card
                  variant='outlined'
                  sx={[
                    styles.photoCard,
                    selectedImageName === image.name && styles.photoCardSelected,
                    isMatching && styles.photoCardMatching
                  ]}
                  key={image.name}
                  ref={setPhotoNodeRef(image.name)}
                  onClick={isMatching ? undefined : () => onSelectImage(image.name)}
                  draggable={!isMatching}
                  onDragStart={(e) => {
                    // Carry the name for native drop targets; effect mirrors a move.
                    e.dataTransfer.setData('text/plain', image.name);
                    e.dataTransfer.effectAllowed = 'move';
                    onImageDragStart(image.name);
                  }}
                  onDragEnd={onImageDragEnd}
                >
                  <IconButton
                    className='photo-delete-btn'
                    aria-label={`Remove ${image.name}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      onRemoveImage(image.name);
                    }}
                    sx={styles.photoDeleteButton}
                  >
                    <Box className='photo-delete-circle' sx={styles.photoDeleteCircle}>
                      <CloseIcon sx={styles.photoDeleteIcon} />
                    </Box>
                  </IconButton>
                  <Box
                    component='img'
                    src={image.thumbnail || undefined}
                    alt={image.name}
                    draggable={false}
                    sx={styles.photoImage}
                  />
                  <Box sx={styles.photoMeta}>
                    <Typography variant='bodySmall' noWrap>
                      {image.name}
                    </Typography>
                    <Typography variant='bodySmall'>Drag or click</Typography>
                  </Box>
                  {isMatching && <Box sx={styles.photoOverlay} />}
                </Card>
              );
            })}
          </Box>
        </>
      )}
    </Card>
  );
};

export default ImagesPanel;
